import { createAction, createSlice, current, PayloadAction } from "@reduxjs/toolkit";
import { QuestionnaireEvaluateResult } from "@repo/api";
import { StepperIndexes } from "../../stepperIndexes";
import { type AMLFormValues } from "../../types/aml";

export enum StepperType {
    /**
     *  Změna strategie spoření
     */
    ChangeInvestmentStrategy = "changeInvestmentStrategy",
    /**
     * Změna potřeb
     */
    PensionQuestionnaire = "pensionQuestionnaire",
    /**
     * Změna příspěvku
     */
    ChangePensionContribution = "changePensionContribution",
}

export interface SmsStepState {
    phoneNumber: string; // phone number selected by user
    transactionId: string; // transaction id received from sending sms
    errorString: string;
}

export interface FundsRow {
    id: number;
    value: number;
}

export interface ChangeContributionState {
    newEmployerContribute: boolean;
    newMonthlyContribution: number;
    currentEmployerContribute: boolean;
    currentMonthlyContribution: number;
}

export interface StepperState {
    // stepper common
    stepIndex: number; // current step index in the questionnaire
    allStepsList: Array<StepperIndexes>; // list of all steps in the questionnaire
    // step specific state
    smsStepState?: SmsStepState;
    smsVerificationStep?: {
        errorString: string;
    };
    editInvestmentStrategyResult?: QuestionnaireEvaluateResult | null;
    existingFundRows?: FundsRow[];
    newFundRows?: FundsRow[];
    sustainabilityId?: string;
    // persisted questionnaire answers between step navigation
    questionnaireAnswers?: Record<string, any>;
    questionnaireRejected: boolean;
    canChangeInvestmentStrategy?: boolean | null;
    questionnaireVersion?: string;
    whatToChangeAnswers?: {
        existingContribution: boolean;
        newContribution: boolean;
    };
    isChangeFundsRequestSuccessful?: boolean | null;
    isChangeContributionSuccessful?: boolean | null;
    AMLIdentificationState?: AMLFormValues;
    changeContributionState?: ChangeContributionState;
    preselectedContribution?: number;
    stepperType: StepperType | undefined;
    contractId: string;
    initializationErrorCode?: "missingContractId";
    lastQuestionnaireSubmitDate: { status: "notLoaded" | "neverSubmitted" | "submitted"; date?: string | null };
}

const stepperInitialState: StepperState = {
    stepIndex: 0,
    allStepsList: [],
    questionnaireRejected: false,
    isChangeFundsRequestSuccessful: null,
    isChangeContributionSuccessful: null,
    stepperType: undefined,
    contractId: "",
    initializationErrorCode: undefined,
    lastQuestionnaireSubmitDate: { status: "notLoaded" },
};

export const stepperSlice = createSlice({
    name: "stepperSlice",
    initialState: stepperInitialState,
    reducers: {
        setExistingFunds: (
            state,
            action: PayloadAction<{
                existingFundRows: StepperState["existingFundRows"];
            }>,
        ) => {
            state.existingFundRows = action.payload.existingFundRows;
        },
        setNewFunds: (
            state,
            action: PayloadAction<{
                newFundRows: StepperState["newFundRows"];
            }>,
        ) => {
            state.newFundRows = action.payload.newFundRows;
        },
        setSmsPhoneNumber: (state, action: PayloadAction<string>) => {
            if (state.smsStepState) {
                state.smsStepState.phoneNumber = action.payload;
            }
        },
        setTransactionId: (state, action: PayloadAction<string>) => {
            if (state.smsStepState) {
                state.smsStepState.transactionId = action.payload;
            }
        },
        // select first step by name that is found in allStepsList
        setStepByName: (state, action: PayloadAction<StepperIndexes>) => {
            const index = current(state).allStepsList.indexOf(action.payload);
            if (index !== -1) state.stepIndex = index;
            state.stepIndex = Math.max(state.stepIndex, 0);
        },
        setFirstStep: (state) => {
            state.stepIndex = 0;
        },
        setNextStep: (state) => {
            state.stepIndex = state.stepIndex + 1;
        },
        setPrevStep: (state) => {
            state.stepIndex = Math.max(state.stepIndex - 1, 0);
        },
        resetQuestionnaire: () => {
            return stepperInitialState;
        },
        initializeQuestionnaire: (state, action: PayloadAction<StepperState>) => {
            if (state.allStepsList.length > 0) return state; // already initialized
            return {
                ...stepperInitialState,
                ...action.payload,
                preselectedContribution: state.preselectedContribution,
            };
        },
        insertStepsIfMissing: (state, action: PayloadAction<{ steps: StepperIndexes[]; index: number }>) => {
            action.payload.steps.forEach((step, stepIndex) => {
                if (state.allStepsList.indexOf(step) === -1) {
                    const targetIndex = Math.min(
                        Math.max(action.payload.index + stepIndex, 0),
                        state.allStepsList.length,
                    );
                    state.allStepsList.splice(targetIndex, 0, step);
                }
            });
            // Initialize smsStepState if SMS steps were added but state wasn't initialized
            if (
                !state.smsStepState &&
                (state.allStepsList.indexOf(StepperIndexes.smsVerificationPage) !== -1 ||
                    state.allStepsList.indexOf(StepperIndexes.phoneNumberPage) !== -1)
            ) {
                state.smsStepState = {
                    phoneNumber: "",
                    transactionId: "",
                    errorString: "",
                };
            }
        },
        removeStepIfPresent: (state, action: PayloadAction<StepperIndexes>) => {
            const index = state.allStepsList.indexOf(action.payload);
            if (index !== -1) state.allStepsList.splice(index, 1);
            state.stepIndex = Math.min(state.stepIndex, state.allStepsList.length - 1);
        },
        removeStepsIfPresent: (state, action: PayloadAction<StepperIndexes[]>) => {
            action.payload.forEach((step) => {
                const index = state.allStepsList.indexOf(step);
                if (index !== -1) state.allStepsList.splice(index, 1);
            });
            state.stepIndex = Math.min(state.stepIndex, state.allStepsList.length - 1);
        },
        // Expands the current sub step by a given number
        subListExpand: (state, action: PayloadAction<number>) => {
            const arr: Array<StepperIndexes> = Array(action.payload).fill(state.allStepsList[state.stepIndex]);
            state.allStepsList.splice(state.stepIndex, 0, ...arr);
        },
        // Changes size of the current sub step to a given size (can even shrink!)
        subListSetSize: (
            state,
            action: PayloadAction<{
                size: number;
                subStepIndex: number;
                subStepsLength: number;
            }>,
        ) => {
            const { size, subStepsLength, subStepIndex } = action.payload;
            const difference = size - subStepsLength;
            const firstSubPos = state.stepIndex - subStepIndex;
            const lastSubPos = firstSubPos + subStepsLength;
            // If the sub step count is smaller, shrink
            if (difference < 0) state.allStepsList.splice(lastSubPos, difference);
            // exapand
            else {
                const arr: Array<StepperIndexes> = Array(difference).fill(state.allStepsList[state.stepIndex]);
                state.allStepsList.splice(lastSubPos, 0, ...arr);
            }
        },
        setEditInvestmentStrategyResult: (state, action: PayloadAction<QuestionnaireEvaluateResult>) => {
            state.editInvestmentStrategyResult = action.payload;
        },
        setSustainability: (state, action: PayloadAction<string>) => {
            state.sustainabilityId = action.payload;
        },
        setQuestionnaireAnswers: (state, action: PayloadAction<Record<string, any>>) => {
            state.questionnaireAnswers = action.payload;
        },
        setQuestionnaireRejected: (state, action: PayloadAction<boolean>) => {
            state.questionnaireRejected = action.payload;
        },
        setCanChangeInvestmentStrategy: (state, action: PayloadAction<boolean | null | undefined>) => {
            state.canChangeInvestmentStrategy = action.payload;
        },
        setQuestionnaireVersion: (state, action: PayloadAction<string | undefined>) => {
            state.questionnaireVersion = action.payload;
        },
        setWhatToChangeAnswers: (state, action: PayloadAction<StepperState["whatToChangeAnswers"]>) => {
            state.whatToChangeAnswers = action.payload;
        },
        setChangeFundsRequestSuccessful: (state, action: PayloadAction<boolean>) => {
            state.isChangeFundsRequestSuccessful = action.payload;
        },
        setChangeContributionSuccessful: (state, action: PayloadAction<boolean>) => {
            state.isChangeContributionSuccessful = action.payload;
        },
        setChangeContributionState: (state, action: PayloadAction<ChangeContributionState>) => {
            state.changeContributionState = action.payload;
        },
        setPreselectedContribution: (state, action: PayloadAction<number | undefined>) => {
            state.preselectedContribution = action.payload;
        },
        setAMLIdentificationState: (state, action: PayloadAction<AMLFormValues>) => {
            state.AMLIdentificationState = action.payload;
        },
        setLastQuestionnaireSubmitDate: (state, action: PayloadAction<string | null | undefined>) => {
            const isoDate = action.payload ?? null;
            state.lastQuestionnaireSubmitDate = {
                status: isoDate ? "submitted" : "neverSubmitted",
                date: isoDate,
            };
        },
    },
});

export const {
    resetQuestionnaire,
    subListExpand,
    subListSetSize,
    setFirstStep,
    setStepByName,
    setNextStep,
    setPrevStep,
    setEditInvestmentStrategyResult,
    initializeQuestionnaire,
    setSmsPhoneNumber,
    setTransactionId,
    setExistingFunds,
    setNewFunds,
    setQuestionnaireRejected,
    setCanChangeInvestmentStrategy,
    setQuestionnaireAnswers,
    setQuestionnaireVersion,
    setSustainability,
    insertStepsIfMissing,
    removeStepIfPresent,
    removeStepsIfPresent,
    setWhatToChangeAnswers,
    setChangeFundsRequestSuccessful,
    setChangeContributionSuccessful,
    setChangeContributionState,
    setPreselectedContribution,
    setAMLIdentificationState,
    setLastQuestionnaireSubmitDate,
} = stepperSlice.actions;

export type InitializeStepperOptions = {
    name: StepperType;
    useAml?: boolean;
    useBankId?: boolean;
    contractId: string;
};
// Saga trigger action (no reducer) to initialize stepper
export const initializeStepperByName = createAction<InitializeStepperOptions>("stepper/initializeStepperByName");

export default stepperSlice.reducer;
